'use client';

import * as React from 'react';

import { ArrowLeft, ArrowRight } from 'lucide-react';
import {
  Carousel as ShadcnCarousel,
  CarouselContent as ShadcnCarouselContent,
  CarouselItem as ShadcnCarouselItem,
  CarouselNext as ShadcnCarouselNext,
  CarouselPrevious as ShadcnCarouselPrevious,
  CarouselDots as ShadcnCarouselDots,
  type CarouselApi,
} from '@/hen-ui/shadcn/carousel';
import { Button as ShadcnButton } from '@/hen-ui/shadcn/button';
import {
  carouselItemVariants,
  carouselNavButtonVariants,
  carouselDotVariants,
  type CarouselItemVariantProps,
  type CarouselNavButtonVariantProps,
  type CarouselDotVariantProps,
} from './carousel.variants';
import { cn } from '@/hen-ui/utils';

export interface CarouselProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof ShadcnCarousel>,
    'children' | 'orientation'
  > {
  children: React.ReactNode;
  showControls?: boolean;
  showDots?: boolean;
}

export interface CarouselItemProps
  extends React.ComponentPropsWithoutRef<typeof ShadcnCarouselItem>,
    CarouselItemVariantProps {
  children?: React.ReactNode;
}

/**
 * Carousel component following HEN Design System standards.
 *
 * A high-level carousel component that automatically includes navigation controls.
 * Children should be CarouselItem components.
 *
 * @example
 * // Basic usage
 * <Carousel>
 *   <CarouselItem><Hero /></CarouselItem>
 *   <CarouselItem><Hero /></CarouselItem>
 *   <CarouselItem><Hero /></CarouselItem>
 * </Carousel>
 *
 * @example
 * // Without controls
 * <Carousel showControls={false} showDots={false}>
 *   <CarouselItem>Slide 1</CarouselItem>
 * </Carousel>
 */
export const Carousel = React.forwardRef<HTMLDivElement, CarouselProps>(
  (
    { className, children, showControls = true, showDots = true, ...props },
    ref,
  ) => {
    return (
      <ShadcnCarousel
        className={cn(
          'relative w-full flex flex-col gap-lg md:gap-d3 lg:gap-xl',
          className,
        )}
        ref={ref}
        {...props}
      >
        <ShadcnCarouselContent>{children}</ShadcnCarouselContent>
        {showControls && (
          <div className='flex items-center justify-center gap-4'>
            <ShadcnCarouselPrevious className='static translate-y-0 border-black hover:text-white hover:bg-black hover:border-white' />
            {showDots && <ShadcnCarouselDots />}
            <ShadcnCarouselNext className='static translate-y-0 border-black hover:text-white hover:bg-black hover:border-white' />
          </div>
        )}
      </ShadcnCarousel>
    );
  },
);
Carousel.displayName = 'Carousel';

export const CarouselItem = React.forwardRef<HTMLDivElement, CarouselItemProps>(
  ({ className, basis, ...props }, ref) => {
    return (
      <ShadcnCarouselItem
        className={cn(carouselItemVariants({ basis }), className)}
        ref={ref}
        {...props}
      />
    );
  },
);
CarouselItem.displayName = 'CarouselItem';

export type { CarouselApi };

export interface CarouselPreviousProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'>,
    CarouselNavButtonVariantProps {}

export const CarouselPrevious = React.forwardRef<
  HTMLButtonElement,
  CarouselPreviousProps
>(({ className, type = 'button', design = 'default', ...props }, ref) => (
  <ShadcnButton
    ref={ref}
    type={type}
    variant='outline'
    size='icon'
    className={cn(carouselNavButtonVariants({ design }), className)}
    {...props}
  >
    <ArrowLeft className='h-4 w-4' aria-hidden='true' />
    <span className='sr-only'>Previous slide</span>
  </ShadcnButton>
));
CarouselPrevious.displayName = 'CarouselPrevious';

export interface CarouselNextProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'>,
    CarouselNavButtonVariantProps {}

export const CarouselNext = React.forwardRef<
  HTMLButtonElement,
  CarouselNextProps
>(({ className, type = 'button', design = 'default', ...props }, ref) => (
  <ShadcnButton
    ref={ref}
    type={type}
    variant='outline'
    size='icon'
    className={cn(carouselNavButtonVariants({ design }), className)}
    {...props}
  >
    <ArrowRight className='h-4 w-4' aria-hidden='true' />
    <span className='sr-only'>Next slide</span>
  </ShadcnButton>
));
CarouselNext.displayName = 'CarouselNext';

export interface CarouselDotsProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>,
    CarouselDotVariantProps {
  count: number;
  activeIndex: number;
  onDotClick?: (index: number) => void;
}

export const CarouselDots = React.forwardRef<HTMLDivElement, CarouselDotsProps>(
  (
    { className, count, activeIndex, onDotClick, design = 'default', ...props },
    ref,
  ) => (
    <div
      ref={ref}
      role='group'
      aria-label='Slide navigation'
      className={cn('flex items-center justify-center gap-5', className)}
      {...props}
    >
      {Array.from({ length: count }).map((_, index) => (
        <button
          key={index}
          type='button'
          aria-current={activeIndex === index ? 'page' : undefined}
          aria-label={`Go to slide ${index + 1}`}
          className={carouselDotVariants({
            design,
            active: activeIndex === index,
          })}
          onClick={() => onDotClick?.(index)}
        />
      ))}
    </div>
  ),
);
CarouselDots.displayName = 'CarouselDots';
